import type { ReactElement } from 'react';
import type { FormOutputFields } from './RebalancingRuleForm';
import { useNavigate, useParams } from 'react-router';
import {
  IPeriodUnit,
  useRebalancingRuleTemplatesSuspenseQuery,
  useUpdateRebalancingRuleTemplateMutation,
  PortfolioRebalancingRulesDocument,
  RebalancingRuleTemplatesDocument,
} from 'generated/graphql';
import isNil from 'lodash/fp/isNil';
import { parseUtcDate, convertDateInUtcToUTCISODate } from 'components/date.utils';
import { useFeedback } from 'components/technical/Feedback/UseFeedback';
import RebalancingRuleForm, { defaultName } from './RebalancingRuleForm';

const UpdateRebalancingRuleTemplateForm = (): ReactElement => {
  const { rebalancingRuleTemplateId } = useParams();
  const { data } = useRebalancingRuleTemplatesSuspenseQuery();

  const rebalancingRuleTemplate = data.portfolio.definitionRuleTemplates.find(
    (rule) => rule.id === rebalancingRuleTemplateId
  );

  if (isNil(rebalancingRuleTemplate)) {
    throw new Error('Rebalancing rule template not found');
  }

  const [updateRebalancingRule] = useUpdateRebalancingRuleTemplateMutation();
  const { showSuccessMessage } = useFeedback();
  const navigate = useNavigate();

  const handleFormSubmit = async ({
    input,
    onErrorAndThrow,
  }: {
    input: FormOutputFields;
    onErrorAndThrow: (error: unknown) => void;
  }): Promise<void> => {
    try {
      await updateRebalancingRule({
        variables: {
          input: {
            id: rebalancingRuleTemplate?.id,
            name: input.name,
            startAt: convertDateInUtcToUTCISODate(input.rebalancingRule.start),
            endAt: null,
            periodUnit: input.rebalancingRule.periodUnit,
            periodValue: input.rebalancingRule.periodValue,
            maxRebalancingCost: null,
            tradingFee: (input.rebalancingRule.tradingFee / 100).toFixed(4),
          },
        },
        refetchQueries: [RebalancingRuleTemplatesDocument, PortfolioRebalancingRulesDocument],
      });

      showSuccessMessage('Updated rebalancing rule template');

      navigate('/app/copilot/lab/rebalacing-rule-templates');
    } catch (e) {
      onErrorAndThrow(e);
    }
  };

  return (
    <RebalancingRuleForm
      defaultValues={{
        name: rebalancingRuleTemplate?.name ?? defaultName,
        rebalancingRule: {
          start: parseUtcDate(rebalancingRuleTemplate.start),
          periodUnit: rebalancingRuleTemplate.periodUnit ?? IPeriodUnit.Month,
          periodValue: rebalancingRuleTemplate.periodValue?.toString() ?? null,
          tradingFee: (Number.parseFloat(rebalancingRuleTemplate.tradingFee) * 100).toFixed(4),
        },
      }}
      onSubmit={handleFormSubmit}
    />
  );
};

export default UpdateRebalancingRuleTemplateForm;
